import { getSubscriptionDisclaimerData } from '@/components/Subscriptions/SubscriptionDisclaimer/utils';
import type { ChargebeeApplePayRecurringPaymentRequest } from '@/types/chargebee';
import type { Customer, Pricing } from '@/types/checkout';
import type { ICheckoutContext } from '@/types/context';
import { BillingFrequency, type Plan } from '@/types/subscription';
import { getApplePayPaymentRequestAmount } from './pricing';
import { generateZendeskSupportURL } from './support';
import { shouldShowZipCodeFieldForTaxCalculation } from './tax';

type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * The mount-time request for the session the shopper is looking at. Its billing
 * agreement is the very ROSCA/FTC sentence the page prints, so the Apple sheet
 * and the checkout state identical terms.
 */
export const buildChargebeeApplePayRecurringRequestForSession = (
  { subscription, pricing, customer, publisherId }: ICheckoutContext,
  t: Translate
): ChargebeeApplePayRecurringPaymentRequest | undefined => {
  const isTaxExcludedCountry = customer ? shouldShowZipCodeFieldForTaxCalculation(customer) : false;
  const disclaimer = getSubscriptionDisclaimerData({ plan: subscription?.plan, pricing }, isTaxExcludedCountry);

  return buildChargebeeApplePayRecurringRequest({
    plan: subscription?.plan,
    pricing,
    customer,
    publisherId,
    billingAgreement: disclaimer
      ? t('subscriptionRenewalDisclaimer', {
          period: t(disclaimer.renewalPeriodKey),
          amount: disclaimer.amount,
          taxes: t(disclaimer.taxesKey),
        })
      : undefined,
  });
};

/**
 * Apple's recurring-payment request for the subscription checkout. Without it
 * Apple hands Chargebee a DPAN, which is bound to the device that authorized
 * it — fine for today's charge, useless for every renewal after it. Chargebee's
 * hosted pages send this themselves; a custom checkout has to.
 *
 * Apple's interval units stop at `day`, so a weekly plan is 7 days.
 */
const BILLING_PERIOD_TO_APPLE_INTERVAL: Record<BillingFrequency, { unit: 'day' | 'month' | 'year'; count: number }> = {
  [BillingFrequency.DAILY]: { unit: 'day', count: 1 },
  [BillingFrequency.WEEKLY]: { unit: 'day', count: 7 },
  [BillingFrequency.MONTHLY]: { unit: 'month', count: 1 },
  [BillingFrequency.YEARLY]: { unit: 'year', count: 1 },
};

interface ChargebeeApplePayRecurringInput {
  plan?: Plan;
  pricing?: Pricing;
  customer?: Customer;
  publisherId?: string;
  /** The ROSCA/FTC renewal sentence, so the sheet says what the page says. */
  billingAgreement?: string;
}

export const buildChargebeeApplePayRecurringRequest = ({
  plan,
  pricing,
  customer,
  publisherId,
  billingAgreement,
}: ChargebeeApplePayRecurringInput): ChargebeeApplePayRecurringPaymentRequest | undefined => {
  const interval = plan ? BILLING_PERIOD_TO_APPLE_INTERVAL[plan.billingFrequency] : undefined;
  const renewalAmount = getRenewalAmount(pricing, customer);
  // Apple requires a description, a schedule and a management URL; an
  // incomplete request is worse than none, so mount without it instead.
  if (!plan || !pricing || !interval || renewalAmount === undefined) return undefined;

  const label = plan.displayName || plan.name;
  return {
    paymentDescription: label,
    regularBilling: {
      label,
      amount: getApplePayPaymentRequestAmount(renewalAmount, pricing),
      paymentTiming: 'recurring',
      recurringPaymentIntervalUnit: interval.unit,
      recurringPaymentIntervalCount: interval.count,
    },
    ...(billingAgreement ? { billingAgreement } : {}),
    // Apple shows this in Wallet as the way out of the subscription. There is
    // no customer portal URL on the wire yet, so it points at the same support
    // desk the checkout footer does — swap it the day the backend sends one.
    managementURL: generateZendeskSupportURL({
      customerId: customer?.id,
      publisherId,
      customerCountryCode: customer?.geoLocator?.countryCode2,
    }),
  };
};

/**
 * Same rule as the on-page renewal disclaimer (ROSCA/FTC): US/CA quote the
 * pre-tax base, everyone else the total. It also keeps the figure stable across
 * a ZIP tax recalc — the schedule is fixed at mount and Apple has no way to
 * update it afterwards.
 */
const getRenewalAmount = (pricing?: Pricing, customer?: Customer): number | undefined => {
  const isTaxExcludedCountry = customer ? shouldShowZipCodeFieldForTaxCalculation(customer) : false;
  const priceBreakdown = pricing?.priceBreakdown;
  return isTaxExcludedCountry ? priceBreakdown?.baseNet : priceBreakdown?.priceTotal;
};
